import gql from 'graphql-tag';

import type { Option } from '@atlaskit/editor-common/extensions';

import { getApolloClient } from '@confluence/graphql';

const labelSearchQuery =
	// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Read https://go/connie-relay-migration-fyi
	gql`
		query LabelSearch($searchTerm: String!) {
			labelSearch(searchText: $searchTerm, limit: 25) {
				otherLabels {
					label
				}
				suggestedLabels {
					label
				}
			}
		}
	`;

const extractResults = (labelResults: { label: string }[]) =>
	labelResults.map((res) => ({
		label: res.label,
		value: res.label,
	}));

const search = async (shouldUsePrefixedLabel: boolean, searchTerm?: string): Promise<Option[]> => {
	let searchTermTrimmed = searchTerm;
	if (shouldUsePrefixedLabel && searchTerm) {
		// Remove the '-' or '+' from the beginning of the label.
		searchTermTrimmed = searchTerm.substring(1);
	}

	const client = getApolloClient();

	const {
		data: { labelSearch },
	} = await client.query({
		query: labelSearchQuery,
		variables: {
			searchTerm: searchTermTrimmed,
		},
	});

	return [
		...extractResults(labelSearch.suggestedLabels),
		...extractResults(labelSearch.otherLabels),
	];
};

const buildResolver =
	(allowsPrefixedLabelFilters: boolean) =>
	async (searchTerm?: string, selectedValue?: string | string[]) => {
		const searchers: Array<Promise<Option[]>> = [];

		// Prefixed labels are labels which start with '-' or '+' to disallow or require that content has the label.
		// These have special behavior: https://support.atlassian.com/confluence-cloud/docs/insert-the-blog-posts-macro/
		const isPrefixedLabel = !!searchTerm?.startsWith('-') || !!searchTerm?.startsWith('+');
		const shouldUsePrefixedLabel = allowsPrefixedLabelFilters && isPrefixedLabel;

		if (searchTerm) {
			searchers.push(search(shouldUsePrefixedLabel, searchTerm));
		}

		if (selectedValue) {
			if (Array.isArray(selectedValue)) {
				searchers.push(
					Promise.resolve(
						selectedValue.map((label) => ({
							label,
							value: label,
						})),
					),
				);
			} else {
				searchers.push(
					Promise.resolve([
						{
							label: selectedValue,
							value: selectedValue,
						},
					]),
				);
			}
		}

		let allSearchResults = (await Promise.all(searchers)).flat();
		if (shouldUsePrefixedLabel) {
			const prefix = searchTerm?.[0] ?? '';
			// Add '-' or '+' to all results if the user is typing a search that starts with a prefix.
			allSearchResults = allSearchResults.map((result) => ({
				label: `${prefix}${result.label}`,
				value: `${prefix}${result.label}`,
			}));
		}

		return allSearchResults;
	};

export const labelResolver = buildResolver(false);
export const prefixedLabelResolver = buildResolver(true);
